import React, { useState, useEffect, useRef, useCallback } from "react";
import useGameBase from "../../hooks/useGameBase";
import SocketContext from "../../context/SocketContext";

export default function MentalMathsGame({
  roomId,
  gameState,
  gamePlayers = [],
  user = {},
}) {
  const socket = React.useContext(SocketContext);
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });

  const socketId = socket?.id || user?.socketId;

  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null); // null | "correct" | "incorrect"
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);
  const [countdownSecs, setCountdownSecs] = useState(null);
  const [nextRoundSecs, setNextRoundSecs] = useState(null);
  const inputRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const nextRoundIntervalRef = useRef(null);
  const prevRoundRef = useRef(null);
  const prevPhaseRef = useRef(null);

  const phase = gameState?.phase || "waiting";
  const puzzle = gameState?.puzzle || null;
  const answeredPlayers = gameState?.answeredPlayers || [];
  const scores = gameState?.scores || {};
  const round = gameState?.round || 0;
  const rounds = gameState?.rounds || 5;
  const countdownAfterFirst = gameState?.countdownAfterFirst || 20;
  const countdownStartAt = gameState?.countdownStartAt || null;
  const nextRoundAt = gameState?.nextRoundAt || null;
  const results = gameState?.results || [];
  const finished = !!gameState?.finished;
  const result = gameState?.result || null;
  const players = Array.isArray(gameState?.players) ? gameState.players : gamePlayers;

  // Reset local state when round changes
  useEffect(() => {
    if (prevRoundRef.current !== round) {
      setAnswer("");
      setFeedback(null);
      setAlreadyAnswered(false);
      prevRoundRef.current = round;
    }
  }, [round]);

  // Sync alreadyAnswered when answeredPlayers changes
  useEffect(() => {
    if (answeredPlayers.includes(socketId)) {
      setAlreadyAnswered(true);
    }
  }, [answeredPlayers, socketId]);

  // Focus input when game is active and player hasn't answered
  useEffect(() => {
    if ((phase === "playing" || phase === "countdown") && !alreadyAnswered) {
      inputRef.current?.focus();
    }
  }, [phase, alreadyAnswered]);

  // Countdown timer
  useEffect(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    if (phase === "countdown" && countdownStartAt) {
      const tick = () => {
        const elapsed = Date.now() - countdownStartAt;
        const remaining = Math.max(0, countdownAfterFirst - Math.floor(elapsed / 1000));
        setCountdownSecs(remaining);
        if (remaining <= 0) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      };
      tick();
      countdownIntervalRef.current = setInterval(tick, 250);
    } else {
      setCountdownSecs(null);
    }

    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [phase, countdownStartAt, countdownAfterFirst]);

  // Next-round countdown
  useEffect(() => {
    if (nextRoundIntervalRef.current) {
      clearInterval(nextRoundIntervalRef.current);
      nextRoundIntervalRef.current = null;
    }
    if (nextRoundAt) {
      const tick = () => {
        const remaining = Math.max(0, Math.ceil((nextRoundAt - Date.now()) / 1000));
        setNextRoundSecs(remaining);
        if (remaining <= 0) {
          clearInterval(nextRoundIntervalRef.current);
          nextRoundIntervalRef.current = null;
        }
      };
      tick();
      nextRoundIntervalRef.current = setInterval(tick, 250);
    } else {
      setNextRoundSecs(null);
    }
    return () => {
      if (nextRoundIntervalRef.current) clearInterval(nextRoundIntervalRef.current);
    };
  }, [nextRoundAt]);

  const handleSubmit = useCallback(async () => {
    const trimmed = answer.trim();
    if (!trimmed || alreadyAnswered) return;
    try {
      const ack = await submitAction({ type: "submitAnswer", answer: trimmed });
      if (ack && ack.correct) {
        setFeedback("correct");
        setAlreadyAnswered(true);
        setAnswer("");
      } else {
        setFeedback("incorrect");        setAnswer("");        setTimeout(() => setFeedback(null), 1200);
      }
    } catch (e) {
      setFeedback("incorrect");
      setTimeout(() => setFeedback(null), 1200);
    }
  }, [answer, alreadyAnswered, submitAction]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  // Format milliseconds to "X.Xs"
  const fmtTime = (ms) => {
    if (ms == null) return "–";
    return (ms / 1000).toFixed(1) + "s";
  };

  // Latest round result
  const latestResult = results.length > 0 ? results[results.length - 1] : null;

  if (!gameState) return <div className="p-4 text-gray-900 dark:text-gray-100">Loading...</div>;

  return (
    <div className="p-4 w-full max-w-xl mx-auto text-gray-900 dark:text-gray-100">

      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Round {round}/{rounds}
        </span>
        <span className="text-xs font-medium px-2 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 capitalize">
          {gameState.difficulty || "medium"}
        </span>
      </div>

      {/* ── Puzzle ── */}
      {(phase === "playing" || phase === "countdown") && puzzle && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
          {/* Start number */}
          <div className="text-center mb-4">
            <span className="text-lg text-gray-500 dark:text-gray-400">Start with</span>
            <div className="text-5xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
              {puzzle.start}
            </div>
          </div>

          {/* Steps */}
          <ol className="space-y-2">
            {(puzzle.steps || []).map((step, i) => (
              <li
                key={i}
                className="flex items-center gap-3 bg-gray-50 dark:bg-gray-700 rounded-lg px-4 py-2"
              >
                <span className="w-6 h-6 flex-shrink-0 rounded-full bg-indigo-500 text-white text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="text-base font-semibold capitalize">{step.label}</span>
              </li>
            ))}
          </ol>

          {/* Countdown banner */}
          {phase === "countdown" && (
            <div className="mt-4 text-center">
              {alreadyAnswered ? (
                <p className="text-green-600 dark:text-green-400 font-semibold text-sm">
                  ✓ You answered! Waiting for others… ({countdownSecs ?? ""}s)
                </p>
              ) : (
                <p className="text-amber-600 dark:text-amber-400 font-semibold text-sm animate-pulse">
                  First answer in! You have {countdownSecs ?? ""}s to answer
                </p>
              )}
            </div>
          )}

          {/* Answer input */}
          {!alreadyAnswered && (
            <div className="mt-5">
              <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">
                Your final answer:
              </label>
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="number"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter answer…"
                  className={`flex-1 p-2 rounded-lg border text-lg font-mono text-center
                    dark:bg-gray-700 dark:text-gray-100 dark:border-gray-500
                    ${feedback === "incorrect"
                      ? "border-red-400 bg-red-50 dark:bg-red-900/30"
                      : "border-gray-300 bg-white"
                    }`}
                />
                <button
                  onClick={handleSubmit}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition-colors"
                >
                  Go!
                </button>
              </div>
              {feedback === "incorrect" && (
                <p className="mt-1 text-sm text-red-500 dark:text-red-400 text-center">
                  ✗ Incorrect — try again!
                </p>
              )}
            </div>
          )}

          {alreadyAnswered && feedback === "correct" && (
            <div className="mt-4 text-center text-green-600 dark:text-green-400 font-bold text-lg">
              ✓ Correct!
            </div>
          )}
        </div>
      )}

      {/* ── Round results ── */}
      {phase === "results" && latestResult && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4">
          <h2 className="text-lg font-bold mb-3 text-center">Round {latestResult.round} Results</h2>

          {/* Show the calculation */}
          <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-700 text-sm">
            <p className="font-medium text-gray-600 dark:text-gray-400 mb-2">The calculation:</p>
            <div className="space-y-1 font-mono">
              {/* Starting number */}
              <div className="flex items-center gap-2">
                <span className="w-5 text-gray-400 dark:text-gray-500 text-xs text-right">★</span>
                <span className="text-gray-500 dark:text-gray-400 text-xs w-28">Start</span>
                <span className="text-indigo-600 dark:text-indigo-400 font-bold text-base">{latestResult.start}</span>
              </div>
              {/* Each step with its result */}
              {(latestResult.steps || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-5 text-gray-400 dark:text-gray-500 text-xs text-right">{i + 1}</span>
                  <span className="text-gray-600 dark:text-gray-300 text-xs w-28 capitalize">{s.label}</span>
                  <span className={`font-bold text-base ${i === (latestResult.steps.length - 1) ? "text-green-600 dark:text-green-400" : "text-gray-800 dark:text-gray-100"}`}>
                    {s.result != null ? s.result : "?"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Per-player times */}
          <div className="space-y-2">
            {(latestResult.perPlayer || [])
              .slice()
              .sort((a, b) => {
                if (a.dnf && !b.dnf) return 1;
                if (!a.dnf && b.dnf) return -1;
                return (a.timeMs || 9e9) - (b.timeMs || 9e9);
              })
              .map((p, i) => {
                const isMe = p.socketId === socketId;
                return (
                  <div
                    key={p.socketId}
                    className={`flex items-center justify-between rounded-lg px-4 py-2
                      ${isMe ? "bg-indigo-50 dark:bg-indigo-900/40 font-semibold" : "bg-gray-50 dark:bg-gray-700"}`}
                  >
                    <div className="flex items-center gap-2">
                      {!p.dnf && (
                        <span className="text-xs w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">
                          {i + 1}
                        </span>
                      )}
                      {p.dnf && (
                        <span className="text-xs w-5 h-5 rounded-full bg-gray-400 text-white flex items-center justify-center">
                          –
                        </span>
                      )}
                      <span>{p.username}{isMe ? " (you)" : ""}</span>
                    </div>
                    <span
                      className={`text-sm font-mono font-bold
                        ${p.dnf ? "text-gray-400 dark:text-gray-500" : "text-green-600 dark:text-green-400"}`}
                    >
                      {p.dnf ? "Did Not Finish" : fmtTime(p.timeMs)}
                    </span>
                  </div>
                );
              })}
          </div>

          {round < rounds && nextRoundSecs != null && (
            <div className="mt-4 text-center">
              <span className="inline-block px-4 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 text-sm font-semibold">
                Next round in {nextRoundSecs}s
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Game finished ── */}
      {finished && result && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 mb-4 text-center">
          <h2 className="text-2xl font-bold mb-2">
            {result.winners && result.winners.includes(socketId)
              ? "🎉 You Won!"
              : result.isTie
              ? "🤝 It's a tie!"
              : `Winner: ${
                  players.find((p) => result.winners && result.winners.includes(p.socketId))
                    ?.username || "Unknown"
                }`}
          </h2>
          <h3 className="font-semibold text-sm mb-3 text-gray-600 dark:text-gray-400">
            Final Scores
          </h3>
          <div className="space-y-1">
            {(result.breakdown || []).map((p, i) => (
              <div
                key={p.socketId}
                className={`flex justify-between items-center px-4 py-1 rounded ${
                  i === 0 ? "bg-yellow-50 dark:bg-yellow-900/30 font-bold" : ""
                }`}
              >
                <span>
                  {i === 0 && "👑 "}
                  {p.username}
                  {p.socketId === socketId ? " (you)" : ""}
                </span>
                <span className="font-mono">{p.score} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Waiting state */}
      {phase === "waiting" && !finished && (
        <div className="text-center py-10 text-gray-400 dark:text-gray-500">
          <p className="text-3xl mb-2">🧮</p>
          <p>Game starting…</p>
        </div>
      )}
    </div>
  );
}

MentalMathsGame.Guide = function MentalMathsGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Mental Maths</h3>
      <ul className="list-disc ml-5 space-y-1 text-sm">
        <li>A starting number is shown along with a series of steps.</li>
        <li>Work through each step in your head — no calculators!</li>
        <li>Type the final answer and press <strong>Go!</strong></li>
        <li>First to get it right scores <strong>10 points</strong>; subsequent correct answers score 5.</li>
        <li>Once the first player answers, others have a short countdown to finish.</li>
        <li>Players who don't answer in time receive <em>Did Not Finish</em>.</li>
        <li>Highest score after all rounds wins.</li>
      </ul>
    </div>
  );
};
